import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

export type AutoTagArticlesDialogParams = {
  // Untagged article ids that will be dispatched.
  articleIds: string[];
  // Total number of articles the user originally selected, including already
  // tagged ones. Used to surface a "skipping N" hint when some selected rows
  // are filtered out — mirrors the backend's silent-drop rule so the count
  // never surprises the user.
  selectedCount: number;
};

type Props = {
  isLoading: boolean;
  onConfirm: (articleIds: string[]) => void;
};

function isAutoTagArticlesDialogParams(params: unknown): params is AutoTagArticlesDialogParams {
  if (!params || typeof params !== 'object') return false;
  const p = params as Record<string, unknown>;
  return Array.isArray(p['articleIds']) && typeof p['selectedCount'] === 'number';
}

export const AutoTagArticlesDialog = ({ isLoading, onConfirm }: Props) => {
  const { t } = useTranslation('knowledge_base');
  const { isOpen, closeModal, getParams } = useModalStore();

  const open = isOpen(MODAL_NAMES.AUTO_TAG_ARTICLES_DIALOG);
  const rawParams = open ? getParams() : null;
  const params = isAutoTagArticlesDialogParams(rawParams) ? rawParams : null;

  const articleIds = params?.articleIds ?? [];
  const selectedCount = params?.selectedCount ?? 0;
  const eligibleCount = articleIds.length;
  const skippedCount = Math.max(selectedCount - eligibleCount, 0);

  const handleRequestClose = () => {
    if (isLoading) return;
    closeModal();
  };

  const handleConfirm = () => {
    if (isLoading || eligibleCount === 0) return;
    onConfirm(articleIds);
  };

  if (!open) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={(value) => !value && handleRequestClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('auto_tag_confirmation_modal.title', { count: eligibleCount })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('auto_tag_confirmation_modal.description')}
            {skippedCount > 0 && (
              <span className="mt-2 block" data-testid="auto-tag-skipped-hint">
                {t('auto_tag_confirmation_modal.skipped_hint', { count: skippedCount })}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Separator />
        <AlertDialogFooter>
          <AlertDialogCancel asChild disabled={isLoading}>
            <Button variant="secondary" disabled={isLoading}>
              {t('button.cancel')}
            </Button>
          </AlertDialogCancel>
          <Button
            variant="dark"
            disabled={isLoading || eligibleCount === 0}
            onClick={handleConfirm}
          >
            {isLoading && <Spinner className="text-white h-4" />}
            {t('auto_tag_confirmation_modal.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
